import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { I_POPUP_EXIT, I_WHITE_CHECK } from '../../../types/images';
import { Col, getItemWithExpiration, Row } from '../../../lib/utils';
import { Body3Light, Body3Regular, CaptionLight, Subtitle1Bold } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import BgColorSelect from './BgColorSelect';
import Scrollbars from 'react-custom-scrollbars-2';
import ColorSquareButton from '../../../components/Button/ColorSquareButton';
import { createMembershipTemplatesAPI } from '../../../api/memberShip';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import MemoTextInput from '../../../components/TextInput/MemoTextInput';
import { useCenterFeatureFlags } from '../../../hooks/useCenterFeatureFlags';
import {
    DEFAULT_SHARE_POOL_LIMITS,
    MembershipTemplateSharePolicy,
    SharePolicyFields,
} from '../../../features/sharedMembership';
import SettlementCategoryField from '../SettlementCategory/SettlementCategoryField';
import { getSettlementClassificationErrorCode, getSettlementClassificationErrorMessage } from '../../../features/settlementClassification';

const ModalOverLay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #00000047;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
`;

const ModalInner = styled.div`
    background: white;
    width: 1080px;
    height: 700px;
    border-radius: 8px;
    box-shadow: 8px 8px 32px 0px #0000007a;
    display: flex;
    flex-direction: column;

    background: #fdfeff;
`;

const HoverImage = styled.img`
    cursor: pointer;
    &:hover {
        opacity: 0.8;
    }
`;

type Props = {
    modalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    createAlert: any;
    onSuccessCreate?: () => void;
};
const ModalRegisterPass = ({ modalVisible, setModalVisible, createAlert, onSuccessCreate }: Props) => {
    const queryClient = useQueryClient();
    const { isSharedMembershipCreatePoolEnabled } = useCenterFeatureFlags();
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));

    const [passName, setPassName] = useState<string>(''); // 수강권 이름
    const [passNumber, setPassNumber] = useState<any>(null); // 수강 횟수

    const [passDate, setPassDate] = useState<any>(null); // 수강권 기간

    const [passTime, setPassTime] = useState<any>(null); // 수업 시간

    const [passPrice, setPassPrice] = useState<string>(''); // 회당 가격

    const [totalPrice, setTotalPrice] = useState<string>(''); // 판매 금액

    const [memo, setMemo] = useState<string>(''); // 메모
    const [bgColor, setBgColor] = useState<any>(['#40CE96', '#1B5AD4']);
    const [isReRegistrationMetricEligible, setIsReRegistrationMetricEligible] = useState(true);
    const [isSharedSaleEnabled, setIsSharedSaleEnabled] = useState(false);
    // 정산 분류. null(없음)이면 키를 보내지 않는다 — 구서버 호환, 서버 기본도 미분류다.
    const [settlementCategoryKey, setSettlementCategoryKey] = useState<string | null>(null);
    const passNumberInputRef = useRef<HTMLInputElement>(null);
    const passDateInputRef = useRef<HTMLInputElement>(null);
    const passTimeInputRef = useRef<HTMLInputElement>(null);
    const passPriceInputRef = useRef<HTMLInputElement>(null);
    const totalPriceInputRef = useRef<HTMLInputElement>(null);

    const handlePassName = (event: ChangeEvent<HTMLInputElement>) => {
        setPassName(event.target.value);
    };

    const handleChangePassNumber = (event: ChangeEvent<HTMLInputElement>) => {
        const numericValue = event.target.value.replace(/\D/g, ''); // 숫자 이외의 문자 제거

        setPassNumber(numericValue);

        // 숫자 변경 후 커서를 숫자 뒤로 이동
        setTimeout(() => {
            if (passNumberInputRef.current) {
                passNumberInputRef.current.setSelectionRange(numericValue.length, numericValue.length);
            }
        }, 0);
    };

    const handleChangePassDate = (event: ChangeEvent<HTMLInputElement>) => {
        const numericValue = event.target.value.replace(/\D/g, ''); // 숫자 이외의 문자 제거
        setPassDate(numericValue);

        // 숫자 변경 후 커서를 숫자 뒤로 이동
        setTimeout(() => {
            if (passDateInputRef.current) {
                passDateInputRef.current.setSelectionRange(numericValue.length, numericValue.length);
            }
        }, 0);
    };

    const handleChangePassTime = (event: ChangeEvent<HTMLInputElement>) => {
        const numericValue = event.target.value.replace(/\D/g, ''); // 숫자 이외의 문자 제거
        // 10분 단위로 변경

        setPassTime(numericValue);

        // 숫자 변경 후 커서를 숫자 뒤로 이동
        setTimeout(() => {
            if (passTimeInputRef.current) {
                passTimeInputRef.current.setSelectionRange(numericValue.length, numericValue.length);
            }
        }, 0);
    };

    // 회당 가격
    const handleChangePassPrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = event.target.value.replace(/[^\d]/g, ''); // 숫자만 남기기

        let formattedValue = '';

        // 뒤에서부터 3자리씩 콤마를 추가하기
        for (let i = rawValue.length - 1; i >= 0; i--) {
            const commaIndex = rawValue.length - 1 - i;
            if (commaIndex > 0 && commaIndex % 3 === 0) {
                formattedValue = ',' + formattedValue; // 콤마 추가
            }
            formattedValue = rawValue.charAt(i) + formattedValue;
        }

        setPassPrice(formattedValue ? `${formattedValue}` : '');

        // 숫자 변경 후 커서를 숫자 뒤로 이동
        setTimeout(() => {
            if (passPriceInputRef.current) {
                passPriceInputRef.current.setSelectionRange(formattedValue.length, formattedValue.length);
            }
        }, 0);
    };
    // 판매 가격
    const handleChangeTotalPrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = event.target.value.replace(/[^\d]/g, ''); // 숫자만 남기기

        let formattedValue = '';

        // 뒤에서부터 3자리씩 콤마를 추가하기
        for (let i = rawValue.length - 1; i >= 0; i--) {
            const commaIndex = rawValue.length - 1 - i;
            if (commaIndex > 0 && commaIndex % 3 === 0) {
                formattedValue = ',' + formattedValue; // 콤마 추가
            }
            formattedValue = rawValue.charAt(i) + formattedValue;
        }

        setTotalPrice(formattedValue ? `${formattedValue}` : '');

        // 숫자 변경 후 커서를 숫자 뒤로 이동
        setTimeout(() => {
            if (totalPriceInputRef.current) {
                totalPriceInputRef.current.setSelectionRange(formattedValue.length, formattedValue.length);
            }
        }, 0);
    };

    const handleKeyDown = (event: any) => {
        // 숫자 키와 백스페이스, Delete 키만 허용
        if (!/[0-9]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete') {
            event.preventDefault();
        }
    };

    const priceToNumber = (price: string): number => {
        const numberString = price.replace(/[^\d]/g, '');
        return parseInt(numberString);
    };

    // 10분 단위로 변경
    const changeTimeToTen = (time: string): string => {
        if (time === '') {
            return '';
        }

        const number = parseInt(time);
        const ten = Math.floor(number / 10) * 10;

        return ten.toString();
    };

    const getSharePolicy = (): MembershipTemplateSharePolicy | undefined => {
        if (!isSharedMembershipCreatePoolEnabled) return undefined;
        if (!isSharedSaleEnabled) {
            return {
                enabled: false,
                maxParticipants: null,
                maxLifetimeParticipants: null,
            };
        }
        // 인원 한도는 화면에서 받지 않는다. 스냅샷도 사실상 무제한 값으로 고정한다.
        return {
            enabled: true,
            ...DEFAULT_SHARE_POOL_LIMITS,
        };
    };

    // API (POST) : 수강권 생성
    const createMembershipTemplates = useMutation({
        mutationFn: async () =>
            await createMembershipTemplatesAPI(
                authorization,
                passName,
                authObject?.centerId,
                passTime,
                priceToNumber(passPrice),
                priceToNumber(totalPrice),
                passNumber,
                bgColor,
                memo,
                passDate,
                isReRegistrationMetricEligible,
                getSharePolicy(),
                settlementCategoryKey ? settlementCategoryKey : undefined,
            ),
        onSuccess: (res) => {
            if (res.status === 201) {
                queryClient.invalidateQueries({
                    queryKey: ['getMembershipTemplatesListAPI'],
                });
                queryClient.invalidateQueries({
                    queryKey: ['getMembershipTemplatesListAPIQuery'],
                });
                console.log('getMembershipTemplatesListAPIQuery 조회');
                onSuccessCreate && onSuccessCreate();

                setModalVisible(false);
            } else if (getSettlementClassificationErrorCode(res)) {
                createAlert('정산 분류 확인', getSettlementClassificationErrorMessage(res), true);
            } else {
                createAlert('수강권 생성 오류', '오류가 지속된다면 라포채널로 문의바랍니다', true);
            }
        },
    });

    // 수강권 생성 버튼 클릭이벤트
    const onClickCreateMemberShipTemplates = () => {
        if (bgColor === null) {
            createAlert('필수 입력', '바탕색을 선택하여 주세요', true);
            return;
        }

        if (passNumber === null || `${passNumber}`.trim().length < 1) {
            createAlert('필수 입력', '수강 횟수를 입력하여 주세요', true);
            return;
        }

        if (passDate === null || `${passDate}`.trim().length < 1) {
            createAlert('필수 입력', '수강권 기간을 입력하여 주세요', true);
            return;
        }

        if (passTime === null || passTime.trim().length < 1) {
            createAlert('필수 입력', '수업 시간을 입력하여 주세요', true);
            return;
        }

        if (passTime % 10 > 0 || passTime.trim().length < 1) {
            createAlert('올바르지 않은 입력', '수업 시간을 10분단위로 입력하여 주세요', true);
            return;
        }

        if (passPrice === null || passPrice.trim().length < 1) {
            createAlert('필수 입력', '회당 가격을 입력하여 주세요', true);
            return;
        }
        createMembershipTemplates.mutate();
    };

    useEffect(() => {
        if (passNumber && passPrice) {
            let number = passNumber.replaceAll('회', '');
            let destroyComma = passPrice.replaceAll(',', '');
            let destroyString = destroyComma.replaceAll('원', '');

            let totalPrice = (parseInt(number) * parseInt(destroyString)).toString();

            let formattedValue = '';

            for (let i = totalPrice.length - 1; i >= 0; i--) {
                const commaIndex = totalPrice.length - 1 - i;
                if (commaIndex > 0 && commaIndex % 3 === 0) {
                    formattedValue = ',' + formattedValue; // 콤마 추가
                }
                formattedValue = totalPrice.charAt(i) + formattedValue;
            }

            setTotalPrice(`${formattedValue}`);
        }
    }, [passNumber, passPrice]);

    // 부모의 스크롤을 막기
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    return (
        <ModalOverLay>
            <ModalInner>
                <Row
                    style={{
                        width: 1080,
                        height: 88,
                        justifyContent: 'space-between',
                        paddingLeft: 48,
                        paddingRight: 20,
                        boxShadow: '0px 2px 6px 0px #00000014',
                    }}
                >
                    <Subtitle1Bold style={{ color: colors.LAVEL_4, marginTop: 36 }}>
                        {'1:1 PT 수강권 상품 새로 만들기'}
                    </Subtitle1Bold>
                    <HoverImage
                        src={I_POPUP_EXIT}
                        style={{ width: 24, height: 24, marginTop: 20 }}
                        onClick={() => setModalVisible(false)}
                    />
                </Row>

                <Scrollbars style={{ width: 1080, height: 612 }}>
                    {/* 수강권 이름 */}
                    <Col>
                        <Body3Regular style={{ marginTop: 32, marginLeft: 80, color: colors.LAVEL_4 }}>
                            {'수강권 이름'}
                        </Body3Regular>

                        <input
                            placeholder="수강권 이름을 입력해주세요!"
                            value={passName}
                            onChange={handlePassName}
                            style={{
                                width: 440,
                                height: 48,
                                borderBottom: '1px solid #A6BFEF',
                                marginLeft: 80,
                                marginTop: 8,
                                paddingLeft: 16,
                                fontSize: 18,
                                fontWeight: 400,
                            }}
                        />
                    </Col>
                    {/* 수강권 색상 */}
                    <BgColorSelect bgColor={bgColor} setBgColor={setBgColor} />

                    {/* 수강 횟수 / 수강권 기간 */}
                    <Row style={{ marginTop: 56, marginLeft: 80 }}>
                        <Col
                            style={{
                                width: 160,
                                height: 80,
                                justifyContent: 'space-between',
                            }}
                        >
                            <Body3Regular style={{ color: colors.LAVEL_4 }}>{'수강 횟수'}</Body3Regular>

                            <Row style={{ marginTop: 8 }}>
                                <input
                                    ref={passNumberInputRef}
                                    type="text"
                                    value={passNumber ? `${passNumber}회` : ''}
                                    maxLength={5}
                                    onChange={handleChangePassNumber}
                                    placeholder="총 회차"
                                    onKeyDown={handleKeyDown}
                                    style={{
                                        width: 160,
                                        height: 48,
                                        borderBottom: '1px solid #A6BFEF',
                                        paddingLeft: 16,
                                        fontSize: 18,
                                        fontWeight: 400,
                                    }}
                                />
                            </Row>
                        </Col>

                        <Col
                            style={{
                                width: 160,
                                height: 80,
                                justifyContent: 'space-between',
                                marginLeft: 80,
                            }}
                        >
                            <Body3Regular style={{ color: colors.LAVEL_4 }}>{'수강권 기간'}</Body3Regular>

                            <Row
                                style={{
                                    marginTop: 8,
                                }}
                            >
                                <input
                                    ref={passDateInputRef}
                                    type="text"
                                    value={passDate ? `${passDate}일` : ''}
                                    maxLength={5}
                                    onChange={handleChangePassDate}
                                    placeholder="90일"
                                    onKeyDown={handleKeyDown}
                                    style={{
                                        width: 160,
                                        height: 48,
                                        borderBottom: '1px solid #A6BFEF',
                                        paddingLeft: 16,
                                        fontSize: 18,
                                        fontWeight: 400,
                                    }}
                                />
                            </Row>
                        </Col>
                    </Row>

                    {/* 수업 시간 */}
                    <Col style={{ width: 340, marginLeft: 80, marginTop: 56 }}>
                        <Body3Regular style={{ color: colors.LAVEL_4 }}>{'수업 시간'}</Body3Regular>
                        <input
                            ref={passTimeInputRef}
                            type="text"
                            value={passTime ? `${passTime}분` : ''}
                            maxLength={4}
                            onChange={handleChangePassTime}
                            placeholder="50분"
                            onKeyDown={handleKeyDown}
                            style={{
                                width: 360,
                                height: 48,
                                borderBottom: '1px solid #A6BFEF',
                                paddingLeft: 16,
                                fontSize: 18,
                                fontWeight: 400,
                                marginTop: 8,
                            }}
                        />
                        <Body3Light style={{ color: colors.LAVEL_1, marginLeft: 16, marginTop: 8 }}>
                            {'10분 단위로 입력해주세요!'}
                        </Body3Light>
                    </Col>

                    {/* 회당 가격 */}
                    <Col style={{ width: 340, marginLeft: 80, marginTop: 56 }}>
                        <Body3Regular style={{ color: colors.LAVEL_4 }}>{'회당 가격(원)'}</Body3Regular>
                        <input
                            ref={passPriceInputRef}
                            type="text"
                            value={passPrice ? `${passPrice}원` : ''}
                            onChange={handleChangePassPrice}
                            placeholder="1회당 가격을 입력해주세요!"
                            onKeyDown={handleKeyDown}
                            maxLength={10}
                            style={{
                                width: 360,
                                height: 48,
                                borderBottom: '1px solid #A6BFEF',
                                paddingLeft: 16,
                                fontSize: 18,
                                fontWeight: 400,
                                marginTop: 8,
                            }}
                        />
                    </Col>

                    {/* 판매 금액 */}
                    <Col style={{ width: 340, marginLeft: 80, marginTop: 56 }}>
                        <Row style={{ alignItems: 'center' }}>
                            <Body3Regular style={{ color: colors.LAVEL_4 }}>{'판매 금액(원)'}</Body3Regular>
                            <CaptionLight style={{ color: colors.LAVEL_1, marginLeft: 8 }}>
                                {'회당 가격과 수강 횟수를 입력하면 자동입력됩니다! '}
                            </CaptionLight>
                        </Row>
                        <input
                            ref={totalPriceInputRef}
                            type="text"
                            value={totalPrice ? `${totalPrice}원` : ''}
                            onChange={handleChangeTotalPrice}
                            placeholder="판매 금액을 입력해주세요!"
                            style={{
                                width: 360,
                                height: 48,
                                borderBottom: '1px solid #A6BFEF',
                                paddingLeft: 16,
                                fontSize: 18,
                                fontWeight: 400,
                                marginTop: 8,
                            }}
                        />
                    </Col>
                    <Col
                        style={{
                            width: 600,
                            marginTop: 56,
                            marginLeft: 80,
                            padding: 20,
                            border: '1px solid #DCE6F8',
                            borderRadius: 8,
                            backgroundColor: '#F7FAFF',
                        }}
                    >
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={isReRegistrationMetricEligible}
                                onChange={(event) => setIsReRegistrationMetricEligible(event.target.checked)}
                                style={{ width: 20, height: 20, marginRight: 12, accentColor: '#1B5AD4' }}
                            />
                            <Body3Regular style={{ color: colors.LAVEL_4 }}>
                                {'재등록 성공률 집계에 포함'}
                            </Body3Regular>
                        </label>
                        <Body3Light style={{ color: colors.LAVEL_1, marginTop: 10, lineHeight: '22px' }}>
                            {
                                '일반 유료 PT 수강권은 포함하세요. OT·체험·서비스·양도 전용 수강권은 체크를 해제해야 통계가 정확합니다.'
                            }
                        </Body3Light>
                    </Col>
                    <SettlementCategoryField
                        centerId={authObject?.centerId}
                        settlementType={authObject?.settlementType}
                        value={settlementCategoryKey}
                        onChange={setSettlementCategoryKey}
                    />
                    {isSharedMembershipCreatePoolEnabled && (
                        <SharePolicyFields
                            enabled={isSharedSaleEnabled}
                            onEnabledChange={setIsSharedSaleEnabled}
                        />
                    )}
                    <Col style={{ marginTop: 56, marginLeft: 80, marginBottom: 37 }}>
                        <Body3Regular className="text-LAVEL_4 mb-[16px]">{'메모'}</Body3Regular>

                        <MemoTextInput
                            value={memo}
                            onChange={(e) => {
                                setMemo(e.target.value);
                            }}
                            placeholder="메모를 남겨보세요"
                            width={600}
                            height={136}
                        />
                    </Col>

                    <ColorSquareButton
                        text="수강권 만들기"
                        callBack={onClickCreateMemberShipTemplates}
                        size="middle"
                        customStyle={{ marginBottom: 36, marginLeft: 896 }}
                    />
                </Scrollbars>
            </ModalInner>
        </ModalOverLay>
    );
};

export default ModalRegisterPass;
